function parseNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatPrice(value, currency) {
  const n = parseNumber(value);
  const fixed = Number.isInteger(n) ? `${n}` : n.toFixed(2);
  return `${fixed} ${currency || "TRY"}`;
}

function escapeHtml(value) {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function likeIconSvg() {
  return `
    <svg class="qr-like-btn__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 1.97-1.63l1.2-7A2 2 0 0 0 19.48 11H14z" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"></path>
    </svg>
  `;
}

function closeIconSvg() {
  return `
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2.1" stroke-linecap="round"></path>
    </svg>
  `;
}

export default class QrMenuInteractions {
  constructor() {
    this.root = document.querySelector(".qr-menu-app");
    if (!this.root) return;

    this.results = this.root.querySelector(".js-qr-results");
    this.categoryTabsRow = this.root.querySelector(".js-qr-category-tabs");
    this.tabs = Array.from(this.root.querySelectorAll(".qr-category-tab"));

    this.payload = this.readPayload();
    this.ui = this.payload.ui && typeof this.payload.ui === "object" ? this.payload.ui : {};
    this.likedSet = new Set();
    this.imageViewer = this.createImageViewer();
    this.isSplitRailTheme =
      this.root.classList.contains("qr-theme-split_rail")
      || this.root.classList.contains("qr-variant-split_rail");
    this.state = {
      category: "all",
      mobileExplorerView: this.isSplitRailTheme ? "grid" : "detail",
    };

    this.bindSlider();
    this.bindCategoryScroller();
    this.bindTabs();
    this.bindDelegation();
    this.bindLikes();
    this.handleResponsiveMode(false);
    this.boundHandleResize = () => this.handleResponsiveMode(true);
    window.addEventListener("resize", this.boundHandleResize, { passive: true });
    this.render();
    this.loadLikedState();
  }

  isMobileExplorerMode() {
    if (!this.isSplitRailTheme) return false;
    return window.matchMedia("(max-width: 979px)").matches;
  }

  handleResponsiveMode(shouldRender = false) {
    const mobileExplorer = this.isMobileExplorerMode();
    const modeChanged = this._lastMobileExplorerMode !== mobileExplorer;
    this._lastMobileExplorerMode = mobileExplorer;
    this.root.classList.toggle("is-mobile-explorer", mobileExplorer);
    if (!mobileExplorer) {
      this.state.mobileExplorerView = "detail";
      if (this.categoryTabsRow) {
        this.categoryTabsRow.classList.remove("is-hidden");
      }
      if (shouldRender && modeChanged && this.results) {
        this.render();
      }
      return;
    }
    if (this.state.category === "all") {
      this.state.mobileExplorerView = "grid";
    }
    if (this.categoryTabsRow) {
      this.categoryTabsRow.classList.add("is-hidden");
    }
    if (shouldRender && modeChanged && this.results) {
      this.render();
    }
  }

  createImageViewer() {
    const viewer = document.createElement("dialog");
    viewer.className = "qr-image-viewer";
    viewer.innerHTML = `
      <button type="button" class="qr-image-viewer__close js-qr-close-viewer" aria-label="${escapeHtml(this.text("close_label", "Close"))}">
        ${closeIconSvg()}
      </button>
      <img class="qr-image-viewer__img" src="" alt="" loading="eager" width="1600" height="900">
    `;
    document.body.appendChild(viewer);
    return viewer;
  }

  openImageViewer(source) {
    if (!this.imageViewer || !source) return;
    const image = this.imageViewer.querySelector(".qr-image-viewer__img");
    if (!image) return;
    image.src = source.src || "";
    image.alt = source.alt || this.text("product_image_alt", "Product image");

    if (typeof this.imageViewer.showModal === "function") {
      this.imageViewer.showModal();
      return;
    }
    this.imageViewer.setAttribute("open", "open");
  }

  closeImageViewer() {
    if (!this.imageViewer) return;
    if (typeof this.imageViewer.close === "function") {
      this.imageViewer.close();
      return;
    }
    this.imageViewer.removeAttribute("open");
  }

  readPayload() {
    const script = document.getElementById("qr-menu-payload");
    if (!script || !script.textContent) return { currency: "TRY", groups: [], ui: {} };
    try {
      const parsed = JSON.parse(script.textContent);
      if (!parsed || !Array.isArray(parsed.groups)) {
        return { currency: "TRY", groups: [], ui: {} };
      }
      return {
        currency: parsed.currency || "TRY",
        groups: parsed.groups,
        ui: parsed.ui && typeof parsed.ui === "object" ? parsed.ui : {},
      };
    } catch {
      return { currency: "TRY", groups: [], ui: {} };
    }
  }

  text(key, fallback, params = {}) {
    const raw = this.ui && typeof this.ui[key] === "string" ? this.ui[key] : "";
    const template = raw || fallback || "";
    return template.replace(/\{(\w+)\}/g, (_match, token) => {
      const value = params[token];
      return value === undefined || value === null ? "" : String(value);
    });
  }

  eachPayloadItem(callback) {
    const groups = Array.isArray(this.payload.groups) ? this.payload.groups : [];
    groups.forEach((group) => {
      (group.items || []).forEach((item) => callback(item));
      (group.children || []).forEach((child) => {
        (child.items || []).forEach((item) => callback(item));
      });
    });
  }

  bindSlider() {
    const slidesWrap = this.root.querySelector(".qr-hero__slides");
    const slides = Array.from(this.root.querySelectorAll("[data-qr-slide]"));
    if (!slidesWrap || slides.length < 2) return;

    const prevButton = this.root.querySelector("[data-qr-slide-prev]");
    const nextButton = this.root.querySelector("[data-qr-slide-next]");
    const dotsRoot = this.root.querySelector("[data-qr-slide-dots]");

    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains("is-active")));
    let timerId = null;

    const update = (nextIndex) => {
      index = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        slide.classList.toggle("is-active", slideIndex === index);
      });
      if (dotsRoot) {
        dotsRoot.querySelectorAll("button").forEach((dot, dotIndex) => {
          const active = dotIndex === index;
          dot.classList.toggle("is-active", active);
          dot.setAttribute("aria-selected", active ? "true" : "false");
        });
      }
    };

    const restartTimer = () => {
      if (timerId) {
        window.clearInterval(timerId);
      }
      timerId = window.setInterval(() => update(index + 1), 4200);
    };

    if (dotsRoot) {
      dotsRoot.innerHTML = "";
      slides.forEach((_, dotIndex) => {
        const dot = document.createElement("button");
        dot.type = "button";
        dot.className = "qr-hero__dot";
        dot.setAttribute("aria-label", this.text("slide_label_template", "Slide {index}", { index: dotIndex + 1 }));
        dot.setAttribute("role", "tab");
        dot.addEventListener("click", () => {
          update(dotIndex);
          restartTimer();
        });
        dotsRoot.appendChild(dot);
      });
    }

    if (prevButton) {
      prevButton.addEventListener("click", () => {
        update(index - 1);
        restartTimer();
      });
    }

    if (nextButton) {
      nextButton.addEventListener("click", () => {
        update(index + 1);
        restartTimer();
      });
    }

    update(index);
    restartTimer();
  }

  bindTabs() {
    this.tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        this.state.category = tab.dataset.category || "all";
        if (this.isMobileExplorerMode()) {
          this.state.mobileExplorerView = this.state.category === "all" ? "grid" : "detail";
        }
        this.tabs.forEach((it) => {
          const active = it === tab;
          it.classList.toggle("is-active", active);
          it.setAttribute("aria-selected", active ? "true" : "false");
        });
        if (typeof tab.scrollIntoView === "function") {
          tab.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
        }
        if (typeof this.updateCategoryScroller === "function") {
          window.setTimeout(() => this.updateCategoryScroller(), 120);
        }
        this.render();
      });
    });
  }

  bindCategoryScroller() {
    const track = this.categoryTabsRow;
    const prev = this.root.querySelector("[data-qr-cat-prev]");
    const next = this.root.querySelector("[data-qr-cat-next]");
    if (!track || !prev || !next) return;

    const step = () => Math.max(180, Math.round(track.clientWidth * 0.75));

    this.updateCategoryScroller = () => {
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      const hasOverflow = max > 2;
      prev.hidden = !hasOverflow;
      next.hidden = !hasOverflow;
      prev.disabled = !hasOverflow || track.scrollLeft <= 2;
      next.disabled = !hasOverflow || track.scrollLeft >= max - 2;
    };

    prev.addEventListener("click", () => {
      track.scrollBy({ left: -step(), behavior: "smooth" });
    });
    next.addEventListener("click", () => {
      track.scrollBy({ left: step(), behavior: "smooth" });
    });

    track.addEventListener("scroll", this.updateCategoryScroller, { passive: true });
    window.addEventListener("resize", this.updateCategoryScroller, { passive: true });

    this.updateCategoryScroller();
  }

  bindDelegation() {
    if (!this.root) return;

    this.root.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const backButton = target.closest(".js-qr-mobile-back");
      if (backButton) {
        this.state.category = "all";
        this.state.mobileExplorerView = "grid";
        this.syncCategoryTabState();
        this.render();
        return;
      }

      const exploreButton = target.closest(".js-qr-mobile-explore");
      if (exploreButton) {
        const category = exploreButton.getAttribute("data-category") || "all";
        this.state.category = category;
        this.state.mobileExplorerView = "detail";
        this.syncCategoryTabState();
        this.render();
        return;
      }

      const zoomImage = target.closest(".js-qr-zoom-image");
      if (zoomImage instanceof HTMLImageElement) {
        event.preventDefault();
        this.openImageViewer({
          src: zoomImage.currentSrc || zoomImage.src,
          alt: zoomImage.alt,
        });
        return;
      }

      const subtabButton = target.closest(".js-qr-subtab");
      if (subtabButton) {
        const subtabs = subtabButton.closest(".qr-subtabs");
        if (!subtabs) return;
        const panelSelector = subtabButton.getAttribute("data-panel");
        if (!panelSelector) return;

        subtabs.querySelectorAll(".js-qr-subtab").forEach((button) => {
          const active = button === subtabButton;
          button.classList.toggle("is-active", active);
          button.setAttribute("aria-selected", active ? "true" : "false");
        });

        const groupRoot = subtabs.closest(".qr-group");
        if (groupRoot) {
          groupRoot.querySelectorAll(".qr-subtabs__panel").forEach((panel) => {
            const active = `#${panel.id}` === panelSelector;
            panel.classList.toggle("is-active", active);
            panel.toggleAttribute("hidden", !active);
          });
        }
        return;
      }

      const openButton = target.closest(".js-qr-open-item");
      if (openButton) {
        const selector = openButton.getAttribute("data-target") || "";
        const dialog = selector ? this.root.querySelector(selector) : null;
        if (dialog) {
          if (typeof dialog.showModal === "function") dialog.showModal();
          else dialog.setAttribute("open", "open");
          const itemId = openButton.getAttribute("data-item-id") || openButton.closest("[data-item-id]")?.getAttribute("data-item-id");
          this.trackItemView(itemId);
          dialog.dataset.openedAt = String(Date.now());
        }
        return;
      }

      const closeButton = target.closest(".js-qr-close-item");
      if (closeButton) {
        const dialog = closeButton.closest("dialog");
        if (!dialog) return;
        this.trackItemDuration(dialog);
        if (typeof dialog.close === "function") dialog.close();
        else dialog.removeAttribute("open");
        return;
      }

      if (target instanceof HTMLDialogElement && target.classList.contains("qr-dialog")) {
        const dialog = target;
        this.trackItemDuration(dialog);
        if (typeof dialog.close === "function") dialog.close();
        else dialog.removeAttribute("open");
        return;
      }

      const variantButton = target.closest(".js-qr-variant");
      if (variantButton) {
        this.applyVariant(variantButton);
      }
    });

    if (this.imageViewer) {
      this.imageViewer.addEventListener("click", (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const closeButton = target.closest(".js-qr-close-viewer");
        if (closeButton) {
          this.closeImageViewer();
          return;
        }

        if (target instanceof HTMLDialogElement && target.classList.contains("qr-image-viewer")) {
          this.closeImageViewer();
        }
      });
    }
  }

  applyVariant(button) {
    const dialog = button.closest("dialog");
    if (!dialog) return;

    const priceBox = dialog.querySelector(".js-qr-dialog-price");
    const oldEl = dialog.querySelector(".js-qr-price-old");
    const newEl = dialog.querySelector(".js-qr-price-new");
    if (!priceBox || !newEl) return;

    const base = parseNumber(priceBox.dataset.base);
    const discountRaw = priceBox.dataset.discount;
    const discount = discountRaw ? parseNumber(discountRaw) : null;
    const delta = parseNumber(button.dataset.delta);
    const currency = button.dataset.currency || priceBox.dataset.currency || "TRY";

    if (discount !== null && discountRaw !== "") {
      if (oldEl) {
        oldEl.hidden = false;
        oldEl.textContent = formatPrice(base + delta, currency);
      }
      newEl.textContent = formatPrice(discount + delta, currency);
      return;
    }

    if (oldEl) {
      oldEl.hidden = true;
      oldEl.textContent = "";
    }
    newEl.textContent = formatPrice(base + delta, currency);
  }

  bindLikes() {
    const clientKey = this.clientKey();
    this.root.addEventListener("click", async (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const button = target.closest(".js-qr-like");
      if (!button) return;

      const itemId = button.dataset.itemId;
      if (!itemId) return;

      try {
        const response = await fetch(`/api/v1/qr-menu/items/${itemId}/like/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ client_key: clientKey }),
        });

        if (!response.ok) throw new Error("like_failed");
        const payload = await response.json();
        const liked = Boolean(payload.liked);
        const delta = Number(payload.delta ?? (liked ? 1 : -1));

        if (liked) this.likedSet.add(String(itemId));
        else this.likedSet.delete(String(itemId));

        const likes = this.bumpLikeCount(itemId, delta);
        this.setLikedUI(itemId, liked, likes, true);

      } catch {
        this.showToast(this.text("like_failed", "Unable to submit like."), "error");
      }
    });
  }


  async trackItemView(itemId) {
    if (!itemId) return;
    try {
      await fetch(`/api/v1/qr-menu/items/${itemId}/view/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
    } catch {}
  }

  async trackItemDuration(dialog) {
    const itemId =
      dialog?.getAttribute?.("data-item-id") ||
      dialog?.id?.replace("qr-item-", "").replace("qr-rec-item-", "") ||
      "";
    const openedAt = Number(dialog?.dataset?.openedAt || 0);
    if (!itemId || !openedAt) return;
    const seconds = Math.max(1, Math.round((Date.now() - openedAt) / 1000));
    try {
      await fetch(`/api/v1/qr-menu/items/${itemId}/duration/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ seconds }),
      });
    } catch {}
  }

  render() {
    if (!this.results) return;

    const currency = this.payload.currency || "TRY";
    const allGroups = Array.isArray(this.payload.groups) ? [...this.payload.groups] : [];
    const mobileExplorer = this.isMobileExplorerMode();
    this.root.classList.toggle("is-mobile-explorer", mobileExplorer);
    if (this.categoryTabsRow) {
      this.categoryTabsRow.classList.toggle("is-hidden", mobileExplorer);
    }

    if (mobileExplorer) {
      if (this.state.mobileExplorerView === "grid" || this.state.category === "all") {
        this.state.mobileExplorerView = "grid";
        this.state.category = "all";
        this.syncCategoryTabState();
        this.results.innerHTML = this.renderMobileCategoryGrid(allGroups);
        if (window.lucide?.createIcons) {
          window.lucide.createIcons();
        }
        return;
      }

      const selectedGroup = allGroups.find((group) => group.slug === this.state.category);
      if (!selectedGroup) {
        this.state.category = "all";
        this.state.mobileExplorerView = "grid";
        this.syncCategoryTabState();
        this.results.innerHTML = this.renderMobileCategoryGrid(allGroups);
        if (window.lucide?.createIcons) {
          window.lucide.createIcons();
        }
        return;
      }

      this.syncCategoryTabState();
      this.results.innerHTML = this.renderMobileCategoryDetail(selectedGroup, currency);
      if (window.lucide?.createIcons) {
        window.lucide.createIcons();
      }
      this.syncLikedButtons();
      return;
    }

    let groups = [...allGroups];

    if (this.state.category !== "all") {
      groups = groups.filter((group) => group.slug === this.state.category);
    }

    const sections = groups
      .map((group) => this.renderGroup(group, currency))
      .join("");

    this.results.innerHTML =
      sections ||
      `<div class="qr-empty">${escapeHtml(this.text("empty_category", "No items found in this category."))}</div>`;

    if (window.lucide?.createIcons) {
      window.lucide.createIcons();
    }
    this.syncLikedButtons();
  }

  syncCategoryTabState() {
    this.tabs.forEach((tab) => {
      const value = tab.dataset.category || "all";
      const active = value === this.state.category;
      tab.classList.toggle("is-active", active);
      tab.setAttribute("aria-selected", active ? "true" : "false");
    });
  }

  countGroupItems(group) {
    const own = Array.isArray(group?.items) ? group.items.length : 0;
    const children = Array.isArray(group?.children) ? group.children : [];
    const childrenCount = children.reduce((sum, child) => {
      const childItems = Array.isArray(child?.items) ? child.items.length : 0;
      return sum + childItems;
    }, 0);
    return own + childrenCount;
  }

  renderMobileCategoryGrid(groups) {
    const validGroups = groups.filter((group) => {
      const own = Array.isArray(group?.items) ? group.items.length : 0;
      const childCount = Array.isArray(group?.children) ? group.children.length : 0;
      return own > 0 || childCount > 0;
    });

    if (!validGroups.length) {
      return `<div class="qr-empty">${escapeHtml(this.text("empty_category", "No items found in this category."))}</div>`;
    }

    const cards = validGroups
      .map((group) => {
        const children = Array.isArray(group.children) ? group.children : [];
        const itemCount = this.countGroupItems(group);
        const subtitle =
          children.length > 0
            ? this.text("mobile_group_with_children", "{count} alt kategori", { count: children.length })
            : this.text("mobile_group_items", "{count} ürün", { count: itemCount });
        return `
          <button
            type="button"
            class="qr-mobile-explorer__card js-qr-mobile-explore"
            data-category="${escapeHtml(group.slug)}"
            aria-label="${escapeHtml(group.name)}"
          >
            <span class="qr-mobile-explorer__card-title">${escapeHtml(group.name)}</span>
            <span class="qr-mobile-explorer__card-subtitle">${escapeHtml(subtitle)}</span>
          </button>
        `;
      })
      .join("");

    return `
      <section class="qr-mobile-explorer" aria-label="${escapeHtml(this.text("menu_tabs_label", "Menu categories"))}">
        ${cards}
      </section>
    `;
  }

  renderMobileCategoryDetail(group, currency) {
    const body = this.renderGroup(group, currency);
    return `
      <section class="qr-mobile-nav">
        <button type="button" class="qr-mobile-nav__back js-qr-mobile-back" aria-label="${escapeHtml(this.text("mobile_back", "Geri"))}">
          ${escapeHtml(this.text("mobile_back", "Geri"))}
        </button>
        <div class="qr-mobile-nav__title-wrap">
          <h3 class="qr-mobile-nav__title">${escapeHtml(group.name)}</h3>
          <p class="qr-mobile-nav__meta">${escapeHtml(this.text("menu_subtitle", "Kategori ve ürünleri keşfedin"))}</p>
        </div>
      </section>
      <section class="qr-mobile-detail">
        ${body || `<div class="qr-empty">${escapeHtml(this.text("empty_category", "No items found in this category."))}</div>`}
      </section>
    `;
  }

  renderGroup(group, currency) {
    const hasOwnItems = Array.isArray(group.items) && group.items.length > 0;
    const hasChildren = Array.isArray(group.children) && group.children.length > 0;

    if (!hasOwnItems && !hasChildren) {
      return "";
    }

    if (hasChildren) {
      const tabEntries = [];
      const childEntries = (group.children || []).filter((child) => Array.isArray(child.items) && child.items.length > 0);
      if (hasOwnItems) {
        tabEntries.push({
          key: "all",
          label: this.text("all_label", "All"),
          category: {
            slug: `${group.slug}-all`,
            name: group.name,
            description: "",
            items: group.items || [],
          },
        });
      }

      childEntries.forEach((child) => {
        tabEntries.push({
          key: String(child.slug || child.id),
          label: String(child.name || ""),
          category: child,
        });
      });

      if (!tabEntries.length) {
        return "";
      }

      const tabButtons = tabEntries
        .map((entry, index) => {
          const panelId = `qr-subpanel-${group.id}-${entry.key}`;
          const active = index === 0;
          return `
            <button
              type="button"
              class="qr-subtab js-qr-subtab${active ? " is-active" : ""}"
              data-panel="#${panelId}"
              role="tab"
              aria-selected="${active ? "true" : "false"}"
            >
              ${escapeHtml(entry.label)}
            </button>
          `;
        })
        .join("");

      const tabPanels = tabEntries
        .map((entry, index) => {
          const panelId = `qr-subpanel-${group.id}-${entry.key}`;
          const active = index === 0;
          return `
            <div class="qr-subtabs__panel${active ? " is-active" : ""}" id="${panelId}"${active ? "" : " hidden"}>
              ${this.renderCategory(entry.category, currency, { isChild: true, showHeader: false })}
            </div>
          `;
        })
        .join("");

      return `
        <section class="qr-group" data-group="${escapeHtml(group.slug)}">
          <div class="qr-subtabs">
            <div class="qr-subtabs__nav" role="tablist" aria-label="${escapeHtml(group.name)} sub categories">
              ${tabButtons}
            </div>
            <div class="qr-subtabs__panels">
              ${tabPanels}
            </div>
          </div>
        </section>
      `;
    }

    return `
      <section class="qr-group" data-group="${escapeHtml(group.slug)}">
        <header class="qr-group__head">
          <h3>${escapeHtml(group.name)}</h3>
          ${group.description ? `<p>${escapeHtml(group.description)}</p>` : ""}
        </header>
        ${this.renderCategory(group, currency, { isChild: false, showHeader: false })}
      </section>
    `;
  }

  renderCategory(category, currency, options = {}) {
    const isChild = Boolean(options.isChild);
    const showHeader = options.showHeader !== false;
    const items = [...(category.items || [])];

    if (!items.length) {
      if (isChild) return "";
      return `<div class="qr-empty">${escapeHtml(this.text("empty_category", "No items found in this category."))}</div>`;
    }

    const itemsHtml = items.map((item) => this.renderItem(item, currency)).join("");

    return `
      <section class="qr-category${isChild ? " qr-category--child" : ""}" data-category="${escapeHtml(category.slug)}">
        ${showHeader ? `
          <header class="qr-category__head">
            <h3>${escapeHtml(category.name)}</h3>
            ${category.description ? `<p>${escapeHtml(category.description)}</p>` : ""}
          </header>
        ` : ""}
        <div class="qr-items">${itemsHtml}</div>
      </section>
    `;
  }

  renderItem(item, currency) {
    const isLiked = this.likedSet.has(String(item.id));
    const oldPrice = item.discounted_price !== null && item.discounted_price !== undefined
      ? `<span class="qr-item__old">${formatPrice(item.price, currency)}</span>`
      : "";

    const hasImage = Boolean(item.image);
    const thumbImage = hasImage
      ? `<img class="qr-item__thumb" src="${escapeHtml(item.image)}" alt="${escapeHtml(item.name)}" loading="lazy" width="160" height="160">`
      : "";

    const variants = Array.isArray(item.variants) && item.variants.length
      ? `<ul class="qr-dialog__variants">${item.variants
          .map((variant) => {
            const prefix = parseNumber(variant.price_delta) > 0 ? "+" : "";
            return `
              <li>
                <button type="button" class="js-qr-variant" data-delta="${parseNumber(variant.price_delta)}" data-currency="${escapeHtml(currency)}">
                  <span>${escapeHtml(variant.name)}</span>
                  <small>${prefix}${formatPrice(variant.price_delta, currency)}</small>
                </button>
              </li>
            `;
          })
          .join("")}</ul>`
      : "";

    const discountRaw = item.discounted_price !== null && item.discounted_price !== undefined
      ? parseNumber(item.discounted_price)
      : "";

    return `
      <article class="qr-item" data-item-id="${item.id}">
        <button type="button" class="qr-item__open js-qr-open-item" data-target="#qr-item-${item.id}">
          <div class="qr-item__main">
            <h4>${escapeHtml(item.name)}</h4>
            ${item.description ? `<p>${escapeHtml(item.description)}</p>` : ""}
            <div class="qr-item__price">
              ${oldPrice}
              <strong>${formatPrice(item.effective_price, currency)}</strong>
            </div>
          </div>
          ${thumbImage}
        </button>
        <div class="qr-item__actions">
          <button
            type="button"
            class="qr-like-btn js-qr-like${isLiked ? " is-liked" : ""}"
            data-item-id="${item.id}"
            aria-pressed="${isLiked ? "true" : "false"}"
            title="${escapeHtml(isLiked ? this.text("liked_label", "Liked") : this.text("like_label", "Like"))}"
          >
            ${likeIconSvg()}
          </button>
        </div>

        <dialog class="qr-dialog" id="qr-item-${item.id}">
          <article class="qr-dialog__card">
            <button type="button" class="qr-dialog__close js-qr-close-item" aria-label="${escapeHtml(this.text("close_label", "Close"))}">
              ${closeIconSvg()}
            </button>
            ${hasImage ? `<img class="qr-dialog__image js-qr-zoom-image" src="${escapeHtml(item.image)}" alt="${escapeHtml(item.name)}" loading="lazy" width="1200" height="675">` : ""}
            <div class="qr-dialog__content">
              <header class="qr-dialog__head">
                <h4>${escapeHtml(item.name)}</h4>
                ${item.description ? `<p>${escapeHtml(item.description)}</p>` : ""}
              </header>
              <div class="qr-dialog__price js-qr-dialog-price" data-base="${parseNumber(item.price)}" data-discount="${discountRaw}" data-currency="${escapeHtml(currency)}">
                ${oldPrice ? `<span class="old js-qr-price-old">${formatPrice(item.price, currency)}</span>` : '<span class="old js-qr-price-old" hidden></span>'}
                <strong class="new js-qr-price-new">${formatPrice(item.effective_price, currency)}</strong>
              </div>
              ${variants}
            </div>
          </article>
        </dialog>
      </article>
    `;
  }

  clientKey() {
    const keyName = "qr_menu_client_key";
    const existing = window.localStorage.getItem(keyName);
    if (existing) return existing;
    const generated = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    window.localStorage.setItem(keyName, generated);
    return generated;
  }

  collectItemIds() {
    const ids = new Set();
    this.eachPayloadItem((item) => {
      if (item && item.id !== undefined && item.id !== null) {
        ids.add(Number(item.id));
      }
    });
    return Array.from(ids).filter((id) => Number.isFinite(id) && id > 0);
  }

  async loadLikedState() {
    const itemIds = this.collectItemIds();
    if (!itemIds.length) return;

    try {
      const response = await fetch("/api/v1/qr-menu/items/liked-state/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_key: this.clientKey(),
          item_ids: itemIds,
        }),
      });
      if (!response.ok) return;

      const payload = await response.json();
      const likedItemIds = Array.isArray(payload?.item_ids) ? payload.item_ids : [];
      this.likedSet = new Set(likedItemIds.map((id) => String(id)));
      this.syncLikedButtons();
    } catch {}
  }

  bumpLikeCount(itemId, delta) {
    const targetId = String(itemId);
    let current = null;

    this.eachPayloadItem((item) => {
      if (String(item.id) !== targetId) return;
      if (current === null) current = parseNumber(item.likes_count);
    });

    if (current === null) {
      const firstCount = this.root.querySelector(`.js-qr-like[data-item-id="${targetId}"] .js-qr-like-count`);
      current = firstCount ? parseNumber(firstCount.textContent) : 0;
    }

    const next = Math.max(0, current + delta);

    this.eachPayloadItem((item) => {
      if (String(item.id) === targetId) {
        item.likes_count = next;
      }
    });

    return next;
  }

  setLikedUI(itemId, liked, likesCount = null, animate = false) {
    const value = String(itemId);
    const buttons = this.root.querySelectorAll(`.js-qr-like[data-item-id="${value}"]`);
    buttons.forEach((button) => {
      button.classList.toggle("is-liked", liked);
      button.setAttribute("aria-pressed", liked ? "true" : "false");
      button.setAttribute(
        "title",
        liked ? this.text("liked_label", "Liked") : this.text("like_label", "Like"),
      );

      if (Number.isFinite(likesCount)) {
        const countEl = button.querySelector(".js-qr-like-count");
        if (countEl) countEl.textContent = String(likesCount);
      }

      if (animate && liked) {
        button.classList.remove("is-animating");
        // Force reflow so repeated likes can replay the animation.
        void button.offsetWidth;
        button.classList.add("is-animating");
      }
    });
  }

  syncLikedButtons() {
    this.root.querySelectorAll(".js-qr-like").forEach((button) => {
      const itemId = button.getAttribute("data-item-id");
      if (!itemId) return;
      this.setLikedUI(itemId, this.likedSet.has(String(itemId)));
    });
  }

  showToast(message, type = "success") {
    const prev = document.querySelector(".contact-toast");
    if (prev) prev.remove();

    const toast = document.createElement("div");
    toast.className = `contact-toast contact-toast--${type}`;
    toast.innerHTML = `<div class="contact-toast__message">${escapeHtml(message)}</div>`;
    document.body.appendChild(toast);

    setTimeout(() => toast.classList.add("--show"), 10);
    setTimeout(() => {
      toast.classList.remove("--show");
      setTimeout(() => toast.remove(), 250);
    }, 2000);
  }
}
